import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO, addDays, differenceInCalendarDays } from "date-fns";
import { mdy, mdyTime } from "@/lib/dateFmt";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { colorForShift } from "@/lib/shifts";

// Cell-level classes — kept tiny/monochrome-ish so black/white printers still
// look readable. Colors print only when the user leaves "background graphics"
// enabled in the print dialog (we ask for it via print-color-adjust).
function cellClasses(codeMeta) {
  if (!codeMeta) return "bg-white text-slate-400";
  const c = colorForShift(codeMeta);
  return `${c.bg} ${c.text} ${c.border}`;
}

export default function SchedulePrintPage() {
  const [params] = useSearchParams();
  const facilityId = params.get("facility_id");
  const ppId = params.get("pp_id");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [codes, setCodes] = useState([]);
  const [openShifts, setOpenShifts] = useState({ days: [] });
  const [facilityLabel, setFacilityLabel] = useState("");
  const [ppLabel, setPpLabel] = useState("");

  useEffect(() => {
    if (!facilityId || !ppId) { setErr("Missing facility or pay period"); setLoading(false); return; }
    const isMonthly = String(ppId).startsWith("mo-");
    (async () => {
      try {
        const schedUrl = isMonthly ? `/schedule-month/${ppId}` : `/schedule/${ppId}`;
        const openUrl  = isMonthly ? `/schedule-month/${ppId}/open-shifts` : `/schedule/${ppId}/open-shifts`;
        const [sched, os, emp, cs, fac, months] = await Promise.all([
          api.get(`${schedUrl}?facility_id=${facilityId}`),
          api.get(`${openUrl}?facility_id=${facilityId}`),
          api.get(`/employees?facility_id=${facilityId}`),
          api.get(`/meta/shift-codes?facility_id=${facilityId}`),
          api.get(`/facilities`).catch(() => ({ data: [] })),
          api.get(`/months?facility_id=${facilityId}`).catch(() => ({ data: [] })),
        ]);
        setSchedule(sched.data);
        setOpenShifts(os.data);
        setEmployees(emp.data);
        setCodes(cs.data);
        const f = (fac.data || []).find(x => x.id === facilityId);
        setFacilityLabel(f?.label || facilityId);
        const m = (months.data || []).find(x => x.id === ppId);
        setPpLabel(m?.label || ppId);
      } catch (e) {
        setErr("Couldn't load schedule to print.");
      } finally {
        setLoading(false);
      }
    })();
  }, [facilityId, ppId]);

  const codeMap = useMemo(
    () => new Map((codes || []).map((c) => [c.code, c])),
    [codes]
  );
  const assignMap = useMemo(() => {
    const m = new Map();
    for (const a of (schedule?.assignments || [])) m.set(`${a.employee_id}|${a.date}`, a);
    return m;
  }, [schedule]);

  const days = useMemo(() => {
    if (!schedule?.start || !schedule?.end) return [];
    const s = parseISO(schedule.start);
    const e = parseISO(schedule.end);
    const n = Math.max(1, differenceInCalendarDays(e, s) + 1);
    return Array.from({ length: n }, (_, i) => addDays(s, i));
  }, [schedule]);

  const openByIso = useMemo(() => {
    const m = new Map();
    (openShifts?.days || []).forEach((d) => m.set(d.date, d));
    return m;
  }, [openShifts]);

  // Auto-fire the print dialog once data loaded — user can cancel to just view.
  useEffect(() => {
    if (!loading && !err && schedule && days.length > 0) {
      const t = setTimeout(() => { window.print(); }, 450);
      return () => clearTimeout(t);
    }
  }, [loading, err, schedule, days.length]);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading print view…</div>;
  if (err) return <div className="min-h-screen grid place-items-center text-red-600">{err}</div>;

  const printedAt = mdyTime(new Date());

  // Font size scales down slightly for months so 30 columns still fit.
  const dayCount = days.length;
  const fontStep =
    dayCount <= 14 ? "text-[10px]" :
    dayCount <= 22 ? "text-[9px]"  :
                     "text-[8px]";
  const cellPad =
    dayCount <= 14 ? "px-1.5 py-1"   :
    dayCount <= 22 ? "px-1 py-[3px]" :
                     "px-[3px] py-[2px]";

  const employeeCol =
    dayCount <= 14 ? "w-[1.6in]" :
    dayCount <= 22 ? "w-[1.3in]" :
                     "w-[1.1in]";

  return (
    <>
      {/* PRINT STYLESHEET — landscape, background colors on, avoid row splits */}
      <style>{`
        @page { size: landscape; margin: 0.35in; }
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .print-root { padding: 0 !important; margin: 0 !important; background: #fff !important; min-height: 0 !important; }
          [data-testid="print-area"] { margin: 0 !important; padding: 0 !important; box-shadow: none !important; max-width: none !important; }
          table.print-schedule { page-break-inside: auto; }
          table.print-schedule thead { display: table-header-group; }
          table.print-schedule tr { page-break-inside: avoid; break-inside: avoid; }
          table.print-schedule tbody tr { break-inside: avoid; }
        }
        table.print-schedule { border-collapse: collapse; width: 100%; table-layout: fixed; }
        table.print-schedule th, table.print-schedule td {
          border: 1px solid #cbd5e1; vertical-align: top; overflow: hidden; word-break: break-word;
        }
      `}</style>

      <div className="min-h-screen bg-slate-100 print-root">
        {/* Screen-only controls */}
        <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={() => window.close()} data-testid="print-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Close
          </Button>
          <div className="text-sm text-slate-500">Print preview — this window will open your printer dialog automatically. Choose "Save as PDF" to download.</div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.print()} data-testid="print-btn">
            <Printer size={14} className="mr-1" /> Print
          </Button>
        </div>

        <div className={`bg-white mx-auto my-4 shadow-sm p-4 max-w-[10.5in] ${fontStep}`}
             data-testid="print-area">
          {/* Header */}
          <div className="flex items-baseline justify-between border-b border-slate-300 pb-2 mb-2">
            <div>
              <div className="font-display font-black text-[16px] tracking-tight text-slate-900" data-testid="print-facility">
                {facilityLabel}
              </div>
              <div className="text-[11px] text-slate-600" data-testid="print-period">
                Pay period: <b>{ppLabel}</b>
                {schedule?.start && schedule?.end && (
                  <> · {mdy(schedule.start)} – {mdy(schedule.end)}</>
                )}
                {schedule?.published ? " · Published" : " · Draft"}
              </div>
            </div>
            <div className="text-[10px] text-slate-500 text-right">
              Printed {printedAt}<br />
              {employees.length} employees · {days.length} days
            </div>
          </div>

          <table className="print-schedule">
            <colgroup>
              <col className={employeeCol} />
              {days.map((_, i) => <col key={i} />)}
            </colgroup>
            <thead>
              {/* Open shifts row — each open slot renders as an amber bubble
                  labelled with the actual shift code so staff picking up
                  extra work can see WHICH shift is open at a glance. */}
              <tr className="bg-amber-50">
                <th className={`${cellPad} text-left font-black uppercase tracking-widest text-[9px] text-amber-900`}>Open shifts</th>
                {days.map((d) => {
                  const iso = format(d, "yyyy-MM-dd");
                  const info = openByIso.get(iso);
                  const openItems = info?.open || [];
                  return (
                    <th key={iso} className={`${cellPad} text-center align-middle`}>
                      {openItems.length > 0 ? (
                        <div className="flex flex-wrap gap-[3px] justify-center items-center leading-none">
                          {openItems.map((c, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center justify-center rounded-full bg-amber-400 border border-amber-600 text-amber-950 font-black tabular-nums"
                              style={{ minWidth: 14, height: 14, padding: "0 3px", fontSize: 8, lineHeight: 1 }}
                            >
                              {c.code}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </th>
                  );
                })}
              </tr>
              {/* Date row */}
              <tr className="bg-slate-100">
                <th className={`${cellPad} text-left font-black uppercase tracking-widest text-[9px] text-slate-700`}>Employee</th>
                {days.map((d) => {
                  const iso = format(d, "yyyy-MM-dd");
                  const isWk = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th key={iso} className={`${cellPad} text-center font-semibold ${isWk ? "bg-slate-200" : ""}`} style={{ whiteSpace: "nowrap" }}>
                      <div className="text-[8px] uppercase tracking-widest text-slate-500">{format(d, "EEE")}</div>
                      <div className="tabular-nums">{format(d, "M/d")}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="align-top" data-testid={`print-row-${emp.id}`}>
                  <td className={`${cellPad} font-semibold text-slate-900`}>
                    <div className="leading-tight">{emp.name}</div>
                    <div className="text-[8px] text-slate-500 leading-tight">
                      {emp.role_label ? `${emp.role_label} · ` : ""}
                      FTE {emp.fte ?? "—"}
                    </div>
                  </td>
                  {days.map((d) => {
                    const iso = format(d, "yyyy-MM-dd");
                    const a = assignMap.get(`${emp.id}|${iso}`);
                    const code = a?.shift_code;
                    const meta = code ? codeMap.get(code) : null;
                    const cls = a ? cellClasses(meta) : "bg-white text-slate-300";
                    // For dense monthly views we drop the times to keep each
                    // cell tidy — code alone is enough at 30+ columns.
                    const showTimes = dayCount <= 16 && a?.start_time && a?.end_time;
                    return (
                      <td key={iso} className={`${cellPad} text-center border ${cls}`}>
                        {a ? (
                          <div className="leading-tight" style={{ whiteSpace: "nowrap" }}>
                            <div className="font-bold">{code}</div>
                            {showTimes && (
                              <div className="text-[7px] tabular-nums opacity-80">{a.start_time}–{a.end_time}</div>
                            )}
                          </div>
                        ) : (
                          <span>·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div className="mt-3 pt-2 border-t border-slate-200 text-[8px] text-slate-500 leading-tight flex flex-wrap gap-x-3 gap-y-1">
            {codes.filter((c) => c.type !== "unavailable").slice(0, 18).map((c) => (
              <span key={c.code} className="inline-flex items-center gap-1">
                <span className={`inline-block px-1 rounded border ${colorForShift(c).bg} ${colorForShift(c).text} ${colorForShift(c).border} font-bold`}>
                  {c.code}
                </span>
                <span>{c.label}{c.start && c.end ? ` (${c.start}–${c.end})` : ""}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
